// packages/app/src/components/home/HomePage.tsx
import Grid from '@material-ui/core/Grid';
import {
  HeaderWorldClock,
  HomePageStarredEntities,
  WelcomeTitle,
  FeaturedDocsCard,
} from '@backstage/plugin-home';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Content, Header, Page } from '@backstage/core-components';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { XkcdComicCard } from 'backstage-plugin-xkcd';
import { makeStyles } from '@material-ui/core';

import { FeatureFlagged } from '@backstage/core-app-api';

import { tools, clockConfigs, timeFormat } from './shared';

import {
  AzureHomePrsCard,
  AzureReviewPrsCard,
  HomePageToolkit,
} from '@internal/backstage-plugin-azure-home-prs';

import {
  HomePageRequestedReviewsCard,
  HomePageYourOpenPullRequestsCard,
} from '@roadiehq/backstage-plugin-github-pull-requests';

import AnnouncementBanner from './Home_components/AnnouncementBanner';
import { PostsTableCard } from '@drodil/backstage-plugin-qeta';
import { OwnershipCard } from '@backstage/plugin-org';
import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityProvider } from '@backstage/plugin-catalog-react';
import { parseEntityRef, type Entity } from '@backstage/catalog-model';
import { useEffect, useState } from 'react';

const languages = ['English', 'Australian', 'Irish Gaelic', 'Malay'];

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '25px',
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
}));

/**
 * HomeUserOwnershipCard
 * Fetches the signed-in user's entity from the catalog and supplies it to OwnershipCard.
 * This avoids misusing OwnershipCard without an Entity context (Clean Code: correct context, SRP).
 */
const HomeUserOwnershipCard = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [entity, setEntity] = useState<Entity | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const { namespace, kind, name } = parseEntityRef(
          identity.userEntityRef,
        );
        const fetched = await catalogApi.getEntityByRef({
          kind,
          namespace,
          name,
        });
        if (mounted) {
          setEntity(fetched ?? undefined);
        }
      } catch (e: any) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, catalogApi]);

  if (loading) return <Progress />;
  if (error || !entity) return null; // Fail silent if no entity (YAGNI: no complex fallback yet)

  return (
    <EntityProvider entity={entity}>
      <OwnershipCard hideRelationsToggle variant="gridItem" />
    </EntityProvider>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  // Use the search bar and starred entities as is
  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Header
          title={<WelcomeTitle language={languages} />}
          pageTitleOverride="Home"
        >
          <HeaderWorldClock
            clockConfigs={clockConfigs}
            customTimeFormat={timeFormat}
          />
        </Header>
        <Content>
          <Grid container justifyContent="center" spacing={6}>
            <AnnouncementBanner />
            <Grid container item xs={12} justifyContent="center">
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Search"
              />
            </Grid>

            <Grid container item xs={12}>
              {/* Left column */}
              <Grid item xs={12} md={8}>
                <PostsTableCard rowsPerPage={5} />
              </Grid>
              {/* Right column with stacked cards */}
              <Grid
                item
                xs={12}
                md={4}
                container
                direction="column"
                spacing={4}
              >
                <Grid item>
                  <HomePageStarredEntities groupByKind />
                </Grid>

                <Grid item>
                  <HomeUserOwnershipCard />
                </Grid>
                <Grid item>
                  <HomePageToolkit title="Toolkit" tools={tools} />
                </Grid>
              </Grid>
            </Grid>

            <FeatureFlagged with="ado-pull-requests">
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <AzureHomePrsCard />
                </Grid>

                <Grid item xs={12} md={6}>
                  <AzureReviewPrsCard />
                </Grid>
              </Grid>
            </FeatureFlagged>

            <FeatureFlagged with="github-pull-requests">
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <HomePageYourOpenPullRequestsCard />
                </Grid>

                <Grid item xs={12} md={6}>
                  <HomePageRequestedReviewsCard />
                </Grid>
              </Grid>
            </FeatureFlagged>

            <Grid container item xs={12}>
              <Grid item xs={12} md={4}>
                <XkcdComicCard />
              </Grid>
              <Grid item xs={12} md={4}>
                <FeaturedDocsCard
                  filter={{
                    'metadata.tags': 'recommended',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
